import StringHelper from '../../../Core/helper/StringHelper.js';
import AbstractCrudManagerMixin from '../../crud/AbstractCrudManagerMixin.js';
import Base from '../../../Core/Base.js';
import AjaxTransport from '../../crud/transport/AjaxTransport.js';
import JsonEncoder from '../../crud/encoder/JsonEncoder.js';
import ObjectHelper from '../../../Core/helper/ObjectHelper.js';

/**
 * @module Scheduler/data/mixin/ProjectCrudManager
 */

/**
 * This mixin provides Crud Manager functionality supporting loading of scheduling engine projects.
 *
 * @mixin
 * @mixes Scheduler/crud/AbstractCrudManagerMixin
 * @mixes Scheduler/crud/transport/AjaxTransport
 * @mixes Scheduler/crud/encoder/JsonEncoder
 */
export default Target => class ProjectCrudManager extends (Target || Base).mixin(AbstractCrudManagerMixin, AjaxTransport, JsonEncoder) {

    //region Config

    static get defaultConfig() {
        return {
            project : null
        };
    }

    startConfigure(config) {
        // process the project first which ingests any configured data sources,
        this.getConfig('project');

        super.startConfigure(config);

        this._changesToClear = new Map();
    }

    async doAutoLoad() {
        const { project } = this;

        // Delay autoLoad to after projects initial commit if configured with a project
        if (project) {
            await project.commitAsync();
        }

        // If the project is destroyed, we don't want to load data
        if (!this.isDestroyed && !project?.isDestroyed) {
            return super.doAutoLoad?.();
        }
    }

    applyProjectResponse(response) {
        const
            me = this,
            { project } = me;

        me.applyingProjectResponse = true;

        if ('$input' in response) {
            response = response.$input;
        }

        if (Object.isFrozen(response)) {
            response = Object.assign({}, response);
        }

        const
            startDateField = project.fieldMap.startDate,
            endDateField   = project.fieldMap.endDate,
            startDate      = ObjectHelper.getPath(response, startDateField.dataSource),
            endDate        = ObjectHelper.getPath(response, endDateField.dataSource);

        // With early rendering engine won't convert project dates in time, do it manually first
        if (typeof startDate === 'string') {
            ObjectHelper.setPath(response, startDateField.dataSource, startDateField.convert(startDate));
        }
        if (typeof endDate === 'string') {
            ObjectHelper.setPath(response, endDateField.dataSource, endDateField.convert(endDate));
        }

        project.setByDataSource(response);

        me._changesToClear.set(me, response);

        me.applyingProjectResponse = false;
    }

    async loadCrudManagerData(response, options = {}, ...args) {
        const
            me = this,
            { project } = me;

        // We don't want to react on store changes while loading them
        me.suspendChangeTracking();

        await super.loadCrudManagerData(response, options, ...args);

        // If there is project data provided
        if (!me.applyingResponse && response?.project) {
            // If the project is delaying its linking to a new graph instance
            // wait till it detaches all records from the old graph
            // and then apply the project data.
            // Otherwise, the project changes we apply here will be overridden when detaching from the old graph.
            // Since the code copies last identifier values from the graph when detaching.
            if (project.delayEnteringReplica && project.hasDataInStores) {
                project.ion({
                    recordsUnlinked : () => {
                        me.suspendChangeTracking();
                        me.applyProjectResponse(response.project);
                        me.resumeChangeTracking();
                    },
                    once : true
                });
            }
            else {
                me.applyProjectResponse(response.project);
            }
        }

        me.resumeChangeTracking();

        if (!me.applyingResponse) {
            // might be no project in scheduler/calendar/taskboard
            await project?.commitAsync();
        }
    }

    async sync() {
        const
            me = this,
            { project } = me;

        // Suspend Crud Manager autoSync to not react on changes during commitAsync() call
        me.suspendAutoSync();
        me.suspendChangeTracking();

        // Make sure data is in a calculated state before syncing
        if (project) {
            await project.commitAsync();
        }

        if (me.isDestroying) {
            return;
        }

        // resume change tracking and autoSync silently
        me.resumeChangeTracking(true);
        me.resumeAutoSync(false);

        return super.sync();
    }

    async applyResponse(request, response, options) {
        const
            me = this,
            { project } = me;

        if (me.isDestroyed || project?.isDestroyed) {
            return;
        }

        me.applyingResponse = true;

        me.trigger('beforeApplyResponse', { request, response, options });

        me.crudStores.forEach(storeDesc => storeDesc.store.suspendFilterOnUpdate());

        const
            responseType = me.trackResponseType && response.type || request.type,
            projectConfig = response?.project;

        if (projectConfig) {
            if (responseType === 'load' && project.delayEnteringReplica && project.hasDataInStores) {
                project.ion({
                    recordsUnlinked : () => {
                        me.suspendChangeTracking();
                        me.applyProjectResponse(projectConfig, request);
                        me.resumeChangeTracking();
                    },
                    once : true
                });
            }
            else {
                me.applyProjectResponse(projectConfig, request);
                // Applying values to the project schedules commitAsync() call
                // we are going to call it manually few lines below.
                // So unschedule to avoid multiple calls.
                project.graph?.unScheduleAutoCommit();
            }
        }

        await super.applyResponse(request, response, options);

        // if we have a project
        if (project) {
            // response can force its type
            const
                responseType = me.trackResponseType && response.type || request.type,
                // Make a boolean flag indicating what has triggered the propagation ("propagatingLoadChanges" or  "propagatingSyncChanges")
                propagationFlag = `propagating${StringHelper.capitalize(responseType)}Changes`;

            me.suspendAutoSync();

            if (responseType === 'sync') {
                const projectChangesToClear = me.getRecordChangesToClear(
                    {
                        store : {
                            modelClass : project.constructor
                        }
                    },
                    request?.pack?.project || {},
                    projectConfig,
                    project.modificationDataToWrite,
                    project
                );

                if (projectChangesToClear) {
                    me._changesToClear.set(project, projectChangesToClear);
                }
            }

            // propagatingLoadChanges/propagatingSyncChanges flag
            me[propagationFlag] = true;

            const loud = project.isInitialCommit && !project.silenceInitialCommit;

            // Wait till calculation gets done
            await project.commitAsync();

            // propagatingLoadChanges/propagatingSyncChanges flag
            me[propagationFlag] = false;

            // for initial commit we still trigger the sync, but not for following
            me.resumeAutoSync?.(loud);

            // Accept changes came from the server (might have been destroyed while waiting above)
            me.commitRespondedChanges?.();
        }

        me.crudStores?.forEach(storeDesc => storeDesc.store.resumeFilterOnUpdate());

        // Trigger if we did not get destroyed while waiting above
        me.trigger?.('applyResponse', { request, response, options });

        me.applyingResponse = false;
    }

    applySyncResponse(...args) {
        const
            me = this,
            stmDisabled = me.project?.stm.disabled;

        if (stmDisabled === false && me.shouldIgnoreRemoteChangesInSTM) {
            me.project.stm.disable();
        }

        me.suspendAutoSync();
        super.applySyncResponse(...args);
        me.resumeAutoSync(false);

        if (stmDisabled === false) {
            me.project.stm.enable();
        }
    }

    shouldClearRecordFieldChange(record, field, value) {
        const oldValue = record.getValue(field);

        field = record.getFieldDefinition(field);

        // If the field value matches the one responded from the server
        // we silently accept it
        return field?.isEqual ? field.isEqual(oldValue, value) : ObjectHelper.isEqual(oldValue, value);
    }

    commitRespondedChanges() {
        const me = this;
        // We silently accept changes came from the server
        me._changesToClear.forEach((changes, record) => {
            Object.entries(changes).forEach(([key, value]) => {
                // If the field value matches the one responded from the server we silently accept it
                // for scheduler, the `record` can be a crud manager instance (which is not a model)
                // so we filter out this case
                if (!(record === me && me.project !== me) && me.shouldClearRecordFieldChange(record, key, value)) {
                    delete record.meta.modified[key];
                }
            });
        });

        me._changesToClear.clear();
    }

    applyChangesToStore(storeDesc, storeResponse, storeRequest, ...rest) {
        const changesMap = super.applyChangesToStore(storeDesc, storeResponse, storeRequest, ...rest);

        // The changes enter graph first but not store until later, clearing changes in StoreChanges might still leave
        // records modified. We need to clean up after the next propagation, so we store record changes in a map for now
        if (changesMap?.size && this.project) {
            for (const [id, changes] of changesMap) {
                const record = storeDesc.store.getById(id);
                record && this._changesToClear.set(record, changes);
            }
        }

        return changesMap;
    }
};
